// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.0.6 — the single registry of onboarding personas.
 *
 * Replaces the hand-maintained `PERSONA_PATHS` array that lived in
 * `onboarding-maturity-service.ts`. That array was `P-VN-01`…`16` while the
 * corpus in `docs/user-validation/personas/` had grown to 35: Sprint 62.0.0's
 * eleven personas, and 62.0.1's EU/UK/global six plus two, never reached the
 * product surface whose whole purpose is personas. Nothing failed, because
 * nothing compared the array to the authored set.
 *
 * `persona-registry-drift.test.ts` is now that comparison. It reads every
 * `**ID:**` in the authored documents and fails **naming the persona** on any
 * gap in either direction, so adding a document without an entry here — or an
 * entry here without a document — is a test failure rather than a silent
 * omission.
 *
 * ## Preserved verbatim
 *
 * `P-VN-01`…`16`'s `persona_label` and `primary_modules` are copied
 * **exactly** from the array this replaces. Sprint 63.35.0 (Q45) moved each
 * persona's `role_description` and `quick_start_title` to the versioned pack
 * `packs/personas/persona-text.json`, which the backend serves: the registry
 * holds no prose that cites an article (`persona-text-ratchet` holds it so).
 * This is an extension, not a re-authoring. Their VI values are the existing
 * approved translations lifted from `admin.onboarding_maturity.persona_p_vn_*`
 * and `qs_*_title`, not new work.
 *
 * ## Why the text lives here rather than in the locale bundles
 *
 * `src/shared/src/i18n/__tests__/index.test.ts` (`deep parity`) requires zero
 * missing and zero extra leaf key paths across all 15 non-EN locales — verified,
 * not assumed. Adding `persona_p_vn_17_label`-style keys for the 19 personas
 * would therefore have forced 38 keys into 14 locales that this sprint is
 * explicitly told not to author, and the only way to make the test pass would
 * have been to paste English into them as though it were translation.
 *
 * So the registry carries `{ en, vi }` and the other locales fall back to EN —
 * the same thing they already do for every string nobody has translated yet,
 * except now it is visible instead of faked. The existing 16 keep their locale
 * keys and their existing precedence in the UI, so nothing regresses for them.
 *
 * ## fix-sprint #431 — labels follow the catalogue
 *
 * "Preserved verbatim" preserved six labels that named the persona wrongly or
 * only in part. P-VN-04 read **Telecom DPO** while its document, and every use
 * case that names it, describe the **Telecom Regulatory Affairs Manager** — the
 * person who answers authority requests for subscriber information, not a data
 * protection officer — so an operator choosing a persona was offered a role the
 * catalogue does not have. Five more were clipped (`Cross-Border & Vendor Gov`,
 * `Cybersecurity & Legal Ops`, `E-Commerce Compliance`, `Department Owner`,
 * `Internal Audit Lead`). Each now reads as its document's heading (P-VN-02 as
 * the heading's first name, `Department Data Owner`), here and in the
 * `persona_p_vn_NN_label` locale keys that take precedence over it; the drift
 * test holds every label to the heading, or to a named short form whose every
 * word the heading contains.
 *
 * ## Sprint 63.28.0 (C3) — revised through the freeze, not around it
 *
 * The P-VN-01…16 entries were reviewed against their documents and the
 * frameworks that name each role today (OQ-4: the freeze is kept, and each
 * changed frozen value moves in the same change as the registry). Seven role
 * descriptions now say what the role does under those frameworks; P-VN-03
 * moved from consent collection to platform registration and authority
 * requests, which is what its document and its use cases describe; P-VN-04
 * left Rights Requests, which handles data subjects rather than authorities;
 * P-VN-14 and P-VN-15 gained the Partner Workspace their multi-client work
 * happens in. Each change and its reason is in the sprint work file's
 * revision table.
 */
export const PERSONA_REGISTRY = [
    // ── Vietnam ───────────────────────────────────────────────────
    // P-VN-01…16 — copied from the array this replaces; revised in Sprint 63.28.0 (C3).
    {
        persona_id: 'P-VN-01',
        jurisdiction: 'VN',
        doc_slug: 'vn-enterprise-dpo',
        persona_label: { en: 'Enterprise DPO', vi: 'DPO doanh nghiệp' },
        primary_modules: [
            'dashboard',
            'rights_requests',
            'data_subjects',
            'assessments',
            'compliance_forms',
        ],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector', 'banking_payments'],
        primary_destinations: [
            'rights_requests',
            'dpias',
            'dossier_filings',
            'dpo_appointments',
            'handbook_assessments',
            'compliance_work_programme',
        ],
    },
    {
        persona_id: 'P-VN-02',
        jurisdiction: 'VN',
        doc_slug: 'vn-department-data-owner',
        persona_label: { en: 'Department Data Owner', vi: 'Chủ sở hữu dữ liệu cấp bộ phận' },
        primary_modules: ['task_management', 'compliance_forms', 'data_mapping'],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'tasks',
            'processing_activities',
            'forms_dashboard',
            'attestation_cycles',
        ],
    },
    {
        persona_id: 'P-VN-03',
        jurisdiction: 'VN',
        doc_slug: 'vn-ecommerce-compliance-manager',
        persona_label: {
            en: 'E-Commerce Compliance Manager',
            vi: 'Quản lý tuân thủ thương mại điện tử',
        },
        primary_modules: ['compliance_forms', 'localization', 'incidents', 'audit_trail'],
        role_family: 'sector_regulatory',
        sectors: ['ecommerce_retail'],
        primary_destinations: [
            'form_catalog',
            'forms_dashboard',
            'sector_licences',
            'seller_register',
            'authority_correspondence',
        ],
    },
    {
        persona_id: 'P-VN-04',
        jurisdiction: 'VN',
        doc_slug: 'vn-telecom-regulatory-affairs-manager',
        persona_label: {
            en: 'Telecom Regulatory Affairs Manager',
            vi: 'Quản lý pháp chế và quan hệ cơ quan quản lý viễn thông',
        },
        primary_modules: ['incidents', 'monitoring', 'localization', 'audit_trail'],
        role_family: 'sector_regulatory',
        sectors: ['telecom_postal'],
        primary_destinations: [
            'incident_authority_requests',
            'disclosure_register',
            'authority_directions',
            'regulator_inquiries',
            'sector_licences',
        ],
    },
    {
        persona_id: 'P-VN-05',
        jurisdiction: 'VN',
        doc_slug: 'vn-data-governance-lead',
        persona_label: { en: 'Data Governance Lead', vi: 'Trưởng quản trị dữ liệu' },
        primary_modules: ['data_mapping', 'classification', 'governance', 'discovery'],
        role_family: 'data_ai_governance',
        sectors: ['cross_sector'],
        primary_destinations: [
            'classification_taxonomies',
            'data_registry',
            'enterprise_data_map',
            'attestation_cycles',
            'data_service_providers',
            'transfer_assessments',
        ],
    },
    {
        persona_id: 'P-VN-06',
        jurisdiction: 'VN',
        doc_slug: 'vn-cybersecurity-legal-operations-lead',
        persona_label: {
            en: 'Cybersecurity & Legal Operations Lead',
            vi: 'Trưởng an ninh mạng & vận hành pháp chế',
        },
        primary_modules: ['incidents', 'incident_ops', 'monitoring', 'vendor_risk'],
        role_family: 'security_cyber',
        sectors: ['cross_sector'],
        primary_destinations: [
            'incidents',
            'authority_directions',
            'incident_authority_requests',
            'disclosure_register',
            'authority_readiness',
            'regulatory_sla',
        ],
    },
    {
        persona_id: 'P-VN-07',
        jurisdiction: 'VN',
        doc_slug: 'vn-ai-governance-lead',
        persona_label: { en: 'AI Governance Lead', vi: 'Trưởng quản trị AI' },
        primary_modules: ['assessments', 'program_governance', 'classification', 'monitoring'],
        role_family: 'data_ai_governance',
        sectors: ['cross_sector'],
        primary_destinations: ['ai_system_register', 'incidents', 'framework_evidence_packs'],
    },
    {
        persona_id: 'P-VN-08',
        jurisdiction: 'VN',
        doc_slug: 'vn-cross-border-transfer-vendor-governance-lead',
        persona_label: {
            en: 'Cross-Border Transfer & Vendor Governance Lead',
            vi: 'Trưởng chuyển dữ liệu cá nhân xuyên biên giới & quản trị nhà cung cấp',
        },
        primary_modules: ['vendor_risk', 'governance', 'localization', 'data_mapping'],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'transfer_assessments',
            'cross_border',
            'vendors',
            'sub_processor_register',
            'contracts',
            'dossier_filings',
        ],
    },
    {
        persona_id: 'P-VN-09',
        jurisdiction: 'VN',
        doc_slug: 'vn-personal-data-incident-breach-response-lead',
        persona_label: { en: 'Breach Response Lead', vi: 'Trưởng ứng phó vi phạm' },
        primary_modules: ['incidents', 'incident_ops', 'rights_requests', 'audit_trail'],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'incidents',
            'breach_notifications',
            'regulatory_sla',
            'post_incident_review',
            'regulator_exports',
        ],
    },
    {
        persona_id: 'P-VN-10',
        jurisdiction: 'VN',
        doc_slug: 'vn-internal-audit-regulatory-inspection-lead',
        persona_label: {
            en: 'Internal Audit & Regulatory Inspection Lead',
            vi: 'Trưởng kiểm toán nội bộ & thanh tra của cơ quan quản lý',
        },
        primary_modules: ['audit_trail', 'assessments', 'compliance_forms', 'program_governance'],
        role_family: 'audit_legal_assurance',
        sectors: ['cross_sector'],
        primary_destinations: [
            'audit_engagements',
            'audit_readiness',
            'evidence_gaps',
            'audit_evidence_packs',
            'enforcement_register',
            'regulator_inquiries',
        ],
    },
    {
        persona_id: 'P-VN-11',
        jurisdiction: 'VN',
        doc_slug: 'vn-hr-employee-data-steward',
        persona_label: { en: 'HR & Employee Data Steward', vi: 'Quản lý dữ liệu nhân sự & nhân viên' },
        primary_modules: [
            'consent',
            'rights_requests',
            'data_subjects',
            'data_mapping',
            'compliance_forms',
            'task_management',
        ],
        // The VI here is the existing approved `qs_hr_consent_title`. It has never
        // rendered: this persona's scenario_id is `qs-hr-consent-registration`, so
        // the UI looks up `qs_hr_consent_registration_title` and misses, falling
        // back to English in all 15 non-EN locales. Carrying the text on the
        // registry entry rather than deriving the key from the scenario_id closes it.
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector', 'education'],
        primary_destinations: [
            'consent_workflows',
            'rights_requests',
            'processing_activities',
            'retention_policies',
            'electronic_agreements',
        ],
    },
    {
        persona_id: 'P-VN-12',
        jurisdiction: 'VN',
        doc_slug: 'vn-multinational-subsidiary-compliance-lead',
        persona_label: {
            en: 'Multinational Subsidiary Compliance Lead',
            vi: 'Trưởng tuân thủ công ty con đa quốc gia',
        },
        primary_modules: [
            'assessments',
            'rights_requests',
            'data_subjects',
            'data_mapping',
            'localization',
            'compliance_forms',
        ],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'dossier_filings',
            'transfer_assessments',
            'consent_workflows',
            'rights_requests',
            'compliance_rollup',
        ],
    },
    {
        persona_id: 'P-VN-13',
        jurisdiction: 'VN',
        doc_slug: 'vn-education-compliance-lead',
        persona_label: { en: 'Education Compliance Lead', vi: 'Trưởng tuân thủ giáo dục' },
        primary_modules: [
            'consent',
            'rights_requests',
            'data_subjects',
            'data_mapping',
            'compliance_forms',
            'monitoring',
        ],
        role_family: 'privacy_data_protection',
        sectors: ['education'],
        primary_destinations: [
            'consent_workflows',
            'rights_requests',
            'dpias',
            'student_records',
            'vendors',
        ],
    },
    {
        persona_id: 'P-VN-14',
        jurisdiction: 'VN',
        doc_slug: 'vn-external-legal-advisor',
        persona_label: { en: 'External Legal Advisor', vi: 'Cố vấn pháp lý bên ngoài' },
        primary_modules: [
            'partner_workspace',
            'compliance_forms',
            'assessments',
            'audit_trail',
            'governance',
            'task_management',
        ],
        role_family: 'audit_legal_assurance',
        sectors: ['legal_services'],
        primary_destinations: ['partner_orgs', 'deliverables', 'document_review', 'regulatory_updates'],
    },
    {
        persona_id: 'P-VN-15',
        jurisdiction: 'VN',
        doc_slug: 'vn-law-firm-operations-manager',
        persona_label: { en: 'Law Firm Operations Manager', vi: 'Quản lý vận hành công ty luật' },
        primary_modules: ['partner_workspace', 'task_management', 'compliance_forms', 'audit_trail'],
        role_family: 'operations_records',
        sectors: ['legal_services'],
        primary_destinations: ['partner_orgs', 'deliverables', 'support_tickets', 'tasks'],
    },
    {
        persona_id: 'P-VN-16',
        jurisdiction: 'VN',
        doc_slug: 'vn-business-owner-data-controller',
        persona_label: { en: 'Business Owner / CEO', vi: 'Chủ doanh nghiệp / Giám đốc điều hành' },
        primary_modules: ['dashboard', 'program_governance', 'audit_trail', 'assessments'],
        role_family: 'executive_business',
        sectors: ['cross_sector'],
        primary_destinations: [
            'dashboard',
            'board_report',
            'compliance_score',
            'executive_summaries',
            'penalty_readiness',
        ],
    },
    // P-VN-17…27 — authored in Sprint 62.0.0, unreachable until now.
    {
        persona_id: 'P-VN-17',
        jurisdiction: 'VN',
        doc_slug: 'vn-manufacturing-quality-traceability-lead',
        persona_label: {
            en: 'Manufacturing Quality & Traceability Lead',
            vi: 'Trưởng chất lượng & truy xuất nguồn gốc sản xuất',
        },
        primary_modules: [
            'data_mapping',
            'classification',
            'compliance_forms',
            'audit_trail',
            'task_management',
        ],
        role_family: 'sector_regulatory',
        sectors: ['manufacturing_quality'],
        primary_destinations: [
            'product_register',
            'batch_serial_register',
            'trace_timeline',
            'conformity_evidence',
            'capa_recall',
            'product_classifications',
        ],
    },
    {
        persona_id: 'P-VN-18',
        jurisdiction: 'VN',
        doc_slug: 'vn-epr-packaging-compliance-lead',
        persona_label: { en: 'EPR & Packaging Compliance Lead', vi: 'Trưởng tuân thủ EPR & bao bì' },
        primary_modules: [
            'compliance_forms',
            'data_mapping',
            'vendor_risk',
            'task_management',
            'audit_trail',
        ],
        role_family: 'sector_regulatory',
        sectors: ['manufacturing_quality', 'chemicals_environment'],
        primary_destinations: ['epr_reconciliation', 'product_register', 'vendors', 'form_catalog'],
    },
    {
        persona_id: 'P-VN-19',
        jurisdiction: 'VN',
        doc_slug: 'vn-ehs-chemical-steward',
        persona_label: {
            en: 'EHS & Chemical Steward',
            vi: 'Quản lý an toàn, sức khỏe, môi trường & hóa chất',
        },
        primary_modules: [
            'data_mapping',
            'classification',
            'vendor_risk',
            'incidents',
            'compliance_forms',
        ],
        role_family: 'sector_regulatory',
        sectors: ['chemicals_environment', 'manufacturing_quality'],
        primary_destinations: ['chemical_register', 'conformity_evidence', 'capa_recall', 'incidents'],
    },
    {
        persona_id: 'P-VN-20',
        jurisdiction: 'VN',
        doc_slug: 'vn-plant-ot-iiot-security-lead',
        persona_label: { en: 'Plant OT / IIoT Security Lead', vi: 'Trưởng an ninh OT / IIoT nhà máy' },
        primary_modules: [
            'discovery',
            'monitoring',
            'vendor_risk',
            'incidents',
            'incident_ops',
            'data_mapping',
        ],
        role_family: 'security_cyber',
        sectors: ['manufacturing_quality'],
        primary_destinations: [
            'ot_asset_profiles',
            'remote_access_assurance',
            'incidents',
            'ai_system_register',
        ],
    },
    {
        persona_id: 'P-VN-21',
        jurisdiction: 'VN',
        doc_slug: 'vn-customs-logistics-compliance-lead',
        persona_label: {
            en: 'Customs & Logistics Compliance Lead',
            vi: 'Trưởng tuân thủ hải quan & logistics',
        },
        primary_modules: [
            'data_mapping',
            'compliance_forms',
            'governance',
            'audit_trail',
            'localization',
        ],
        role_family: 'sector_regulatory',
        sectors: ['logistics_customs'],
        primary_destinations: [
            'shipment_register',
            'customs_evidence',
            'inspections_holds',
            'transfer_assessments',
        ],
    },
    {
        persona_id: 'P-VN-22',
        jurisdiction: 'VN',
        doc_slug: 'vn-courier-parcel-operations-lead',
        persona_label: {
            en: 'Courier / Parcel Operations Lead',
            vi: 'Trưởng vận hành chuyển phát / bưu kiện',
        },
        primary_modules: [
            'data_mapping',
            'data_subjects',
            'compliance_forms',
            'rights_requests',
            'governance',
        ],
        role_family: 'operations_records',
        sectors: ['telecom_postal', 'logistics_customs'],
        primary_destinations: [
            'postal_authorisations',
            'parcel_register',
            'delivery_evidence',
            'logistics_claims',
        ],
    },
    {
        persona_id: 'P-VN-23',
        jurisdiction: 'VN',
        doc_slug: 'vn-payments-ekyc-aml-evidence-lead',
        persona_label: {
            en: 'Payments, eKYC & AML Evidence Lead',
            vi: 'Trưởng bằng chứng thanh toán, eKYC & phòng chống rửa tiền',
        },
        primary_modules: [
            'data_mapping',
            'classification',
            'assessments',
            'compliance_forms',
            'audit_trail',
            'vendor_risk',
        ],
        role_family: 'sector_regulatory',
        sectors: ['banking_payments', 'fintech'],
        primary_destinations: [
            'payment_services',
            'ekyc_evidence',
            'aml_programme',
            'suspicious_activity_cases',
            'supervisory_reporting',
            'payments_retention',
        ],
    },
    {
        persona_id: 'P-VN-24',
        jurisdiction: 'VN',
        doc_slug: 'vn-public-data-steward',
        persona_label: { en: 'Public Data Steward', vi: 'Quản lý dữ liệu công' },
        primary_modules: [
            'data_mapping',
            'classification',
            'governance',
            'compliance_forms',
            'program_governance',
        ],
        role_family: 'data_ai_governance',
        sectors: ['public_sector'],
        primary_destinations: [
            'dataset_catalog',
            'data_sharing_register',
            'open_data_releases',
            'data_service_providers',
        ],
    },
    {
        persona_id: 'P-VN-25',
        jurisdiction: 'VN',
        doc_slug: 'vn-health-data-privacy-lead',
        persona_label: { en: 'Health Data Privacy Lead', vi: 'Trưởng quyền riêng tư dữ liệu y tế' },
        primary_modules: ['data_mapping', 'classification', 'data_subjects', 'assessments', 'consent'],
        role_family: 'privacy_data_protection',
        sectors: ['health'],
        primary_destinations: ['processing_activities', 'dpias', 'rights_requests', 'access_log'],
    },
    {
        persona_id: 'P-VN-26',
        jurisdiction: 'VN',
        doc_slug: 'vn-state-secrets-restricted-artifact-custodian',
        persona_label: {
            en: 'State Secrets & Restricted Artifact Custodian',
            vi: 'Người quản lý bí mật nhà nước & tài liệu hạn chế',
        },
        primary_modules: [
            'classification',
            'access_accountability',
            'audit_trail',
            'incidents',
            'vendor_risk',
        ],
        role_family: 'security_cyber',
        sectors: ['public_sector'],
        primary_destinations: ['regulatory_artifacts', 'restricted_access', 'incidents', 'access_log'],
    },
    {
        persona_id: 'P-VN-27',
        jurisdiction: 'VN',
        doc_slug: 'vn-civil-cryptography-trust-service-lead',
        persona_label: {
            en: 'Civil Cryptography & Trust Service Lead',
            vi: 'Trưởng mật mã dân sự & dịch vụ tin cậy',
        },
        primary_modules: [
            'compliance_forms',
            'vendor_risk',
            'assessments',
            'audit_trail',
            'governance',
        ],
        role_family: 'security_cyber',
        sectors: ['trust_cryptography'],
        primary_destinations: [
            'trust_providers',
            'sector_licences',
            'product_classifications',
            'electronic_agreements',
            'electronic_submissions',
        ],
    },
    /*
     * P-VN-28 — authored in Sprint 62.7.0, alongside the domain it owns.
     *
     * The 62.7.0 coverage review named the owning role for the e-commerce
     * tax / e-invoice overlay as "Platform Tax Evidence Lead"
     * (`complianceone-framework-functionality-coverage-review.md:83`) and no such
     * persona existed — the only one of that review's named domain owners with no
     * registry entry, no persona document and no use case. `P-VN-03` is the
     * nearest, and it is about registration dossiers: platform classification,
     * official form labelling, amendment procedures, submission-portal choice.
     * None of its ten validation questions is answered by transaction and
     * document reconciliation, so naming it the acceptance owner would attribute
     * an acceptance proof to a persona this domain does not touch.
     *
     * The Vietnamese label deliberately reads `chứng từ thuế ... nền tảng số`
     * rather than `bằng chứng thuế nền tảng`: the latter is a `vi_deprecated`
     * rendering in `agent-os/terminology/registry.en-vi.json`
     * (id `platform-tax-evidence-applicability-assessment`), superseded by
     * `nghĩa vụ chứng từ thuế của nền tảng số`. `chứng từ` is also the
     * instrument's own word — Decree 70/2025 is *về hóa đơn, chứng từ*.
     */
    {
        persona_id: 'P-VN-28',
        jurisdiction: 'VN',
        doc_slug: 'vn-platform-tax-evidence-lead',
        persona_label: {
            en: 'Platform Tax Evidence Lead',
            vi: 'Trưởng hồ sơ chứng từ thuế nền tảng số',
        },
        primary_modules: [
            'data_mapping',
            'monitoring',
            'compliance_forms',
            'governance',
            'audit_trail',
        ],
        role_family: 'sector_regulatory',
        sectors: ['ecommerce_retail'],
        primary_destinations: [
            'seller_register',
            'transaction_reconciliation',
            'authority_correspondence',
            'regulatory_artifacts',
        ],
    },
    /*
     * P-VN-29 and P-VN-30 — Sprint 62.15.0, appended with the decree-wave census.
     *
     * Both existed as a gap of the same shape: the framework was on the tree and
     * its owner was a placeholder. `VN_TRANSPORT_JOURNEY_MONITORING_DECREE_319_2026`
     * pointed at the customs lead, who has no transport journey, and the securities
     * overlay pointed at the banking lead, who has no securities journey — so both
     * codes read as covered while naming somebody else's work. The census check
     * that found it derives the upcoming set from the packs rather than from a
     * list, so a seventh upcoming code fails rather than escaping.
     */
    {
        persona_id: 'P-VN-29',
        jurisdiction: 'VN',
        doc_slug: 'vn-transport-journey-monitoring-operator',
        persona_label: {
            en: 'Transport Journey-Monitoring Operator',
            vi: 'Cán bộ vận hành giám sát hành trình vận tải',
        },
        primary_modules: ['data_mapping', 'monitoring', 'governance', 'audit_trail', 'localization'],
        role_family: 'operations_records',
        sectors: ['transport'],
        primary_destinations: [
            'conformity_evidence',
            'retention_policies',
            'regulatory_frameworks',
            'penalty_readiness',
        ],
    },
    {
        persona_id: 'P-VN-30',
        jurisdiction: 'VN',
        doc_slug: 'vn-securities-capital-markets-data-compliance-lead',
        persona_label: {
            en: 'Securities & Capital Markets Data Compliance Lead',
            vi: 'Trưởng bộ phận tuân thủ dữ liệu chứng khoán và thị trường vốn',
        },
        primary_modules: [
            'governance',
            'compliance_forms',
            'data_mapping',
            'audit_trail',
            'program_governance',
        ],
        role_family: 'sector_regulatory',
        sectors: ['securities'],
        primary_destinations: [
            'regulatory_disclosures',
            'account_data_disclosures',
            'regulated_accounts',
            'retention_policies',
            'regulator_inquiries',
        ],
    },
    /*
     * P-VN-31 and P-VN-32 — Sprint 62.16.0, appended with the September 2026
     * sector-instrument census. Two new populations with no existing owner: the
     * amended Publishing Law reaches publishers and the platforms that distribute
     * publications, and Decree 284/2026 reaches crypto-asset service providers
     * under the market pilot. Neither is a re-anchoring of an existing persona.
     */
    {
        persona_id: 'P-VN-31',
        jurisdiction: 'VN',
        doc_slug: 'vn-digital-publishing-platform-content-compliance-lead',
        persona_label: {
            en: 'Digital Publishing & Platform Content Compliance Lead',
            vi: 'Trưởng bộ phận tuân thủ xuất bản số và nội dung nền tảng',
        },
        primary_modules: [
            'compliance_forms',
            'governance',
            'audit_trail',
            'localization',
            'program_governance',
        ],
        role_family: 'sector_regulatory',
        sectors: ['publishing_media'],
        primary_destinations: [
            'publications',
            'legal_representatives',
            'publication_assessments',
            'authority_directions',
        ],
    },
    {
        persona_id: 'P-VN-32',
        jurisdiction: 'VN',
        doc_slug: 'vn-crypto-asset-service-provider-compliance-lead',
        persona_label: {
            en: 'Crypto-Asset Service Provider Compliance Lead',
            vi: 'Trưởng bộ phận tuân thủ của tổ chức cung cấp dịch vụ tài sản mã hóa',
        },
        primary_modules: [
            'compliance_forms',
            'governance',
            'audit_trail',
            'monitoring',
            'localization',
        ],
        role_family: 'sector_regulatory',
        sectors: ['crypto_assets', 'fintech'],
        primary_destinations: [
            'sector_licences',
            'marketing_approvals',
            'investor_verifications',
            'account_data_disclosures',
            'regulatory_disclosures',
            'aml_programme',
        ],
    },
    /*
     * P-VN-33 and P-VN-34 — 2026-10-04, appended with the Decree 372/2026/NĐ-CP
     * intake's user-validation gate. The owner asked the gate to cover seven
     * roles; five map to existing personas (P-VN-17 widened to traders, P-VN-21,
     * P-VN-03, P-VN-10). Two have no existing owner: a conformity-assessment or
     * certification body is a named certificate issuer (Điều 8 khoản 3 điểm a)
     * and supplies its own result data (Điều 21 khoản 3 điểm d), and the IT
     * owner of the connection route, keys, receipts and verification records is
     * not the quality lead. Neither is a re-anchoring of an existing persona.
     */
    {
        persona_id: 'P-VN-33',
        jurisdiction: 'VN',
        doc_slug: 'vn-conformity-assessment-certification-body-lead',
        persona_label: {
            en: 'Conformity Assessment & Certification Body Compliance Lead',
            vi: 'Trưởng bộ phận tuân thủ của tổ chức đánh giá sự phù hợp, tổ chức chứng nhận',
        },
        primary_modules: ['compliance_forms', 'governance', 'audit_trail', 'localization'],
        role_family: 'audit_legal_assurance',
        sectors: ['manufacturing_quality', 'food'],
        primary_destinations: ['conformity_certificates', 'conformity_evidence'],
    },
    {
        persona_id: 'P-VN-34',
        jurisdiction: 'VN',
        doc_slug: 'vn-traceability-integration-evidence-custodian',
        persona_label: {
            en: 'Traceability Systems Integration & Evidence Custodian',
            vi: 'Phụ trách tích hợp hệ thống truy xuất nguồn gốc và lưu giữ bằng chứng',
        },
        primary_modules: [
            'compliance_forms',
            'governance',
            'vendor_risk',
            'incident_ops',
            'audit_trail',
        ],
        role_family: 'operations_records',
        sectors: ['manufacturing_quality', 'logistics_customs', 'food'],
        primary_destinations: [
            'system_inventory',
            'trace_timeline',
            'conformity_certificates',
            'incidents',
        ],
    },
    /*
     * P-VN-35, P-VN-36 and P-VN-37 — 2026-10-10, the Decree 382/2026/NĐ-CP
     * intake's persona and use-case gate. The applicant company, the credit
     * institution or foreign-bank branch sponsor, and the solution's information-
     * security architect are the actors the decree names; none maps to an existing
     * persona without re-anchoring one.
     */
    {
        persona_id: 'P-VN-35',
        jurisdiction: 'VN',
        doc_slug: 'vn-fintech-controlled-test-applicant',
        persona_label: {
            en: 'Fintech Controlled-Test Applicant & Solution Owner',
            vi: 'Công ty Fintech đăng ký tham gia thử nghiệm giải pháp và chủ giải pháp',
        },
        primary_modules: ['compliance_forms', 'governance', 'audit_trail', 'localization'],
        role_family: 'sector_regulatory',
        sectors: ['fintech'],
        primary_destinations: ['controlled_testing_cases', 'form_catalog', 'tasks'],
    },
    {
        persona_id: 'P-VN-36',
        jurisdiction: 'VN',
        doc_slug: 'vn-credit-institution-foreign-bank-test-sponsor',
        persona_label: {
            en: 'Credit Institution & Foreign-Bank Branch Test Sponsor',
            vi: 'Tổ chức tín dụng và chi nhánh ngân hàng nước ngoài đăng ký tham gia thử nghiệm',
        },
        primary_modules: [
            'compliance_forms',
            'governance',
            'vendor_risk',
            'audit_trail',
            'localization',
        ],
        role_family: 'sector_regulatory',
        sectors: ['banking_payments', 'fintech'],
        primary_destinations: ['controlled_testing_cases', 'aml_programme', 'vendors', 'contracts'],
    },
    {
        persona_id: 'P-VN-37',
        jurisdiction: 'VN',
        doc_slug: 'vn-fintech-solution-information-security-architect',
        persona_label: {
            en: 'Fintech Solution Information-Security Architect',
            vi: 'Kiến trúc sư an toàn hệ thống thông tin của giải pháp Fintech',
        },
        primary_modules: ['compliance_forms', 'governance', 'audit_trail', 'incident_ops'],
        role_family: 'security_cyber',
        sectors: ['fintech', 'banking_payments'],
        primary_destinations: ['controlled_testing_cases', 'data_flows', 'system_inventory'],
    },
    /*
     * P-VN-38 to P-VN-42 — 2026-10-10, appended with the Circular 53/2026/TT-NHNN
     * banking AI-governance gate (docs/user-validation/circular-53-banking-ai-gate.md,
     * section 5). The Circular requires three independent lines of protection for
     * AI, an independent model validation, a human re-review of complained-of
     * automated decisions, a human override and pause for live channels, and an
     * annual inventory review. Each of the five roles has no existing owner: the
     * first-line system owner (CIO / AI product owner), the independent validator,
     * the complaints lead, the channel supervisor and the second-line compliance
     * officer who keeps the bank-wide register. P-VN-07 classifies one system and
     * P-VN-10 audits, so neither is a re-anchoring. The use cases they serve are
     * UC-VN-58 to UC-VN-64, mapped in use-case-registry.ts.
     */
    {
        persona_id: 'P-VN-38',
        jurisdiction: 'VN',
        doc_slug: 'vn-ai-system-owner-cio-product-owner',
        persona_label: {
            en: 'AI System Owner (CIO / AI Product Owner)',
            vi: 'Chủ sở hữu hệ thống AI (Giám đốc công nghệ thông tin / Chủ sản phẩm AI)',
        },
        primary_modules: ['assessments', 'governance', 'compliance_forms', 'audit_trail'],
        role_family: 'data_ai_governance',
        sectors: ['banking_payments'],
        primary_destinations: ['ai_system_register', 'change_review', 'remediation_plans', 'incidents'],
    },
    {
        persona_id: 'P-VN-39',
        jurisdiction: 'VN',
        doc_slug: 'vn-model-risk-independent-validation-lead',
        persona_label: {
            en: 'Model Risk & Independent Validation Lead',
            vi: 'Trưởng bộ phận rủi ro mô hình và thẩm định độc lập',
        },
        primary_modules: ['assessments', 'governance', 'audit_trail', 'compliance_forms'],
        role_family: 'data_ai_governance',
        sectors: ['banking_payments'],
        primary_destinations: ['ai_system_register', 'remediation_plans', 'governance_approvals'],
    },
    {
        persona_id: 'P-VN-40',
        jurisdiction: 'VN',
        doc_slug: 'vn-customer-complaints-redress-lead',
        persona_label: {
            en: 'Customer Complaints & Redress Lead',
            vi: 'Trưởng bộ phận giải quyết khiếu nại khách hàng',
        },
        primary_modules: ['incident_ops', 'governance', 'compliance_forms', 'audit_trail'],
        role_family: 'operations_records',
        sectors: ['banking_payments'],
        primary_destinations: ['ai_system_register', 'rights_requests', 'tasks'],
    },
    {
        persona_id: 'P-VN-41',
        jurisdiction: 'VN',
        doc_slug: 'vn-branch-contact-centre-operations-supervisor',
        persona_label: {
            en: 'Branch & Contact-Centre Operations Supervisor',
            vi: 'Giám sát viên vận hành chi nhánh và trung tâm chăm sóc khách hàng',
        },
        primary_modules: ['monitoring', 'incident_ops', 'governance', 'audit_trail'],
        role_family: 'operations_records',
        sectors: ['banking_payments'],
        primary_destinations: ['ai_system_register', 'incidents', 'training'],
    },
    {
        persona_id: 'P-VN-42',
        jurisdiction: 'VN',
        doc_slug: 'vn-bank-compliance-officer-second-line',
        persona_label: {
            en: 'Second Line Compliance Officer',
            vi: 'Cán bộ tuân thủ ngân hàng, tuyến bảo vệ thứ hai',
        },
        primary_modules: ['program_governance', 'governance', 'assessments', 'audit_trail'],
        role_family: 'audit_legal_assurance',
        sectors: ['banking_payments'],
        primary_destinations: [
            'ai_system_register',
            'raci_matrix',
            'board_report',
            'regulator_inquiries',
            'monitoring_programs',
        ],
    },
    /*
     * P-VN-43, P-VN-44 and P-VN-45 — 2026-10-10, appended with the food-sector
     * intake's user-validation gate (Circular 63/2026/TT-BCT and Circular
     * 11/2026/TT-BCT). Reused personas cover the testing and certification bodies
     * (P-VN-33), the integration provider's IT owner (P-VN-34), importers' customs
     * records (P-VN-21) and regulator-inspection response (P-VN-10). Three roles
     * have no existing owner: a food manufacturer or processor (P-VN-17 covers
     * goods in general and stops before food-safety certificates and the
     * one-step-back record), a food trader, distributor or retailer, and the
     * administrator of a food establishment's master data and access grants.
     */
    {
        persona_id: 'P-VN-43',
        jurisdiction: 'VN',
        doc_slug: 'vn-food-manufacturing-processing-compliance-lead',
        persona_label: {
            en: 'Food Manufacturing & Processing Compliance Lead',
            vi: 'Trưởng bộ phận tuân thủ sản xuất và chế biến thực phẩm',
        },
        primary_modules: [
            'compliance_forms',
            'governance',
            'incident_ops',
            'audit_trail',
            'task_management',
        ],
        role_family: 'sector_regulatory',
        sectors: ['food'],
        primary_destinations: [
            'product_register',
            'batch_serial_register',
            'trace_timeline',
            'conformity_evidence',
            'capa_recall',
        ],
    },
    {
        persona_id: 'P-VN-44',
        jurisdiction: 'VN',
        doc_slug: 'vn-food-trading-distribution-retail-operations-lead',
        persona_label: {
            en: 'Food Trading, Distribution & Retail Operations Lead',
            vi: 'Trưởng bộ phận vận hành kinh doanh, phân phối và bán lẻ thực phẩm',
        },
        primary_modules: ['compliance_forms', 'governance', 'incident_ops', 'audit_trail'],
        role_family: 'operations_records',
        sectors: ['food', 'ecommerce_retail'],
        primary_destinations: [
            'batch_serial_register',
            'shipment_register',
            'trace_timeline',
            'capa_recall',
            'customs_evidence',
        ],
    },
    {
        persona_id: 'P-VN-45',
        jurisdiction: 'VN',
        doc_slug: 'vn-food-traceability-data-administrator',
        persona_label: {
            en: 'Food Traceability Data Administrator',
            vi: 'Quản trị viên dữ liệu truy xuất nguồn gốc thực phẩm',
        },
        primary_modules: ['access_accountability', 'organizations', 'compliance_forms', 'audit_trail'],
        role_family: 'operations_records',
        sectors: ['food'],
        primary_destinations: ['organizations', 'product_register', 'roles', 'access_reviews'],
    },
    /*
     * P-VN-47, P-VN-48 and P-VN-49 — 2026-10-22, the October 2026 intakes'
     * persona and use-case gate (docs/user-validation/
     * october-2026-intakes-persona-usecase-gate.md, section 8.2; owner question
     * OQ-G-1). A people's credit fund has no compliance department and no
     * existing persona; a State body's specialised cybersecurity unit acting as
     * system owner is neither the State data steward (P-VN-24) nor the enterprise
     * cybersecurity lead (P-VN-06); and the product team exploiting national data
     * through a request, an incubator or a data centre's controlled testing is
     * neither the enterprise data-governance lead (P-VN-05) nor the banking
     * controlled-test applicant (P-VN-35). P-VN-46 is reserved by the Sprint
     * 63.26.0 close-out report and is not registered here. The three Vietnamese
     * persona_label values are R-44 approved (2026-10-22, Compliance Manager
     * review by the `compliance` sub-agent, batch
     * agent-os/terminology/batches/2026-10-22-october-intakes-persona-titles.csv,
     * registry.en-vi.json, scope `onboarding`); use them verbatim. The other
     * Vietnamese strings below (role descriptions, quick-start titles) are drafts
     * written for these entries and have not been through a terminology batch.
     */
    {
        persona_id: 'P-VN-47',
        jurisdiction: 'VN',
        doc_slug: 'vn-peoples-credit-fund-compliance-lead',
        persona_label: {
            en: "People's Credit Fund Compliance & Branch-Liaison Lead",
            vi: 'Đầu mối tuân thủ của quỹ tín dụng nhân dân và làm việc với chi nhánh khu vực',
        },
        primary_modules: ['compliance_forms', 'localization', 'task_management', 'audit_trail'],
        role_family: 'sector_regulatory',
        sectors: ['banking_payments'],
        primary_destinations: ['regulatory_frameworks', 'form_catalog', 'tasks'],
    },
    {
        persona_id: 'P-VN-48',
        jurisdiction: 'VN',
        doc_slug: 'vn-state-body-cybersecurity-assurance-lead',
        persona_label: {
            en: 'State Body Cybersecurity Assurance Lead',
            vi: 'Đầu mối bảo đảm an ninh mạng của cơ quan nhà nước',
        },
        primary_modules: ['assessments', 'governance', 'access_accountability', 'audit_trail'],
        role_family: 'security_cyber',
        sectors: ['public_sector'],
        primary_destinations: ['regulatory_frameworks', 'system_inventory', 'restricted_access'],
    },
    {
        persona_id: 'P-VN-49',
        jurisdiction: 'VN',
        doc_slug: 'vn-data-innovation-exploitation-lead',
        persona_label: {
            en: 'Data Innovation & National-Data Exploitation Lead',
            vi: 'Người phụ trách hoạt động sáng tạo, khai thác dữ liệu quốc gia',
        },
        primary_modules: ['data_mapping', 'compliance_forms', 'governance', 'audit_trail'],
        role_family: 'data_ai_governance',
        sectors: ['technology_saas'],
        primary_destinations: ['controlled_testing_cases', 'data_registry', 'form_catalog'],
    },
    /*
     * P-VN-50 — 2026-10-24, the Circulars 169/2026/TT-BCA and 170/2026/TT-BCA
     * persona and use-case gate (docs/user-validation/
     * identity-biometrics-qcvn-16-17-persona-usecase-gate.md). The supplier or
     * integrator of voice and iris capture equipment and software serving the
     * national Identity Database has no existing persona: the conformity body
     * (P-VN-33) tests and certifies rather than supplies, and the payments eKYC
     * lead (P-VN-23) is a private onboarding role. The Vietnamese strings are
     * drafts written for this entry and have not been through a terminology batch.
     */
    {
        persona_id: 'P-VN-50',
        jurisdiction: 'VN',
        doc_slug: 'vn-identity-biometric-capture-supplier-integration-lead',
        persona_label: {
            en: 'Identity-Database Biometric Capture Supplier & Integration Compliance Lead',
            vi: 'Đầu mối tuân thủ của nhà cung cấp, tích hợp thiết bị và phần mềm thu nhận sinh trắc học phục vụ Cơ sở dữ liệu căn cước',
        },
        primary_modules: ['localization', 'compliance_forms', 'data_mapping', 'audit_trail'],
        role_family: 'sector_regulatory',
        sectors: ['public_sector', 'technology_saas'],
        primary_destinations: [
            'biometric_capture_components',
            'regulatory_frameworks',
            'form_catalog',
            'system_inventory',
        ],
    },
    /*
     * P-VN-51 — 2026-10-24, Sprint 62.56.0, on the owner's answer to the gate's
     * OQ-B-3 (docs/user-validation/identity-biometrics-qcvn-16-17-persona-usecase-gate.md
     * §11): police units are a customer segment. A collecting, managing or using
     * unit (§1.2), a provincial or city police organising implementation (§4.3)
     * and the focal unit (§4.2.1) operate capture components rather than supply
     * them, so P-VN-50 does not cover them. The title avoids "Đầu mối", which the
     * text reserves for the focal unit.
     */
    {
        persona_id: 'P-VN-51',
        jurisdiction: 'VN',
        doc_slug: 'vn-police-unit-biometric-collection-evidence-lead',
        persona_label: {
            en: 'Police-Unit Biometric Collection & Evidence Compliance Lead',
            vi: 'Cán bộ phụ trách tuân thủ và hồ sơ thu nhận sinh trắc học tại đơn vị Công an',
        },
        primary_modules: ['localization', 'data_mapping', 'assessments', 'audit_trail'],
        role_family: 'sector_regulatory',
        sectors: ['public_sector'],
        primary_destinations: [
            'biometric_capture_components',
            'biometric_supervision_records',
            'regulatory_frameworks',
            'processing_activities',
            'system_inventory',
        ],
    },
    /*
     * P-VN-52 — 2026-10-26, the owner's decision OQ-86-1, recorded in
     * docs/user-validation/circular-86-persona-usecase-gate.md §7 and
     * circular-152-persona-usecase-gate.md §11. The finance function that pays,
     * books and keeps the payment documents, the figures of a refund dossier and
     * the debt instruments the customs authority states, and the fee and charge
     * lines of a licence or appraisal application. The customs and logistics
     * compliance lead (P-VN-21) files with customs and the licensing lead
     * (P-VN-27) files the licence dossier; neither keeps the payment ledger. The
     * Vietnamese label is the one in agent-os/scripts/help/persona-titles.vi.json;
     * the other Vietnamese strings are drafts and have not been through a
     * terminology batch. Written as P-VN-51 and renumbered P-VN-52 on the merge
     * with `main` (2026-10-27), whose Sprint 62.56.0 took P-VN-51 for the
     * police-unit persona above.
     */
    {
        persona_id: 'P-VN-52',
        jurisdiction: 'VN',
        doc_slug: 'vn-finance-tax-accounting-lead',
        persona_label: {
            en: 'Finance & Tax Accounting Lead',
            vi: 'Trưởng bộ phận tài chính & kế toán thuế',
        },
        primary_modules: ['compliance_forms', 'governance', 'audit_trail', 'localization'],
        role_family: 'sector_regulatory',
        sectors: ['logistics_customs', 'technology_saas'],
        primary_destinations: [
            'customs_tax_payments',
            'customs_tax_dossiers',
            'licence_applications',
            'sector_licences',
        ],
    },
    // ── European Union / EEA ──────────────────────────────────────
    {
        persona_id: 'P-EU-01',
        jurisdiction: 'EU',
        doc_slug: 'eu-data-protection-officer',
        persona_label: { en: 'EU/EEA Data Protection Officer', vi: 'Cán bộ bảo vệ dữ liệu EU/EEA' },
        primary_modules: [
            'data_mapping',
            'assessments',
            'rights_requests',
            'incidents',
            'compliance_forms',
            'data_subjects',
        ],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'processing_activities',
            'dpias',
            'breach_notifications',
            'rights_requests',
            'dpo_appointments',
        ],
    },
    {
        persona_id: 'P-EU-02',
        jurisdiction: 'EU',
        doc_slug: 'eu-eprivacy-consent-manager',
        persona_label: {
            en: 'ePrivacy & Digital Consent Manager',
            vi: 'Quản lý đồng ý số & ePrivacy',
        },
        primary_modules: [
            'consent',
            'consent_properties',
            'privacy_scanner',
            'monitoring',
            'audit_trail',
        ],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'cmp_integration',
            'consent_properties',
            'marketing_consent',
            'suppression_register',
            'consent_evidence',
            'privacy_monitoring',
        ],
    },
    {
        persona_id: 'P-EU-03',
        jurisdiction: 'EU',
        doc_slug: 'eu-financial-entity-ict-risk-lead',
        persona_label: {
            en: 'Financial Entity ICT & Third-Party Risk Lead',
            vi: 'Trưởng rủi ro CNTT & bên thứ ba của tổ chức tài chính',
        },
        primary_modules: [
            'vendor_risk',
            'incidents',
            'incident_ops',
            'program_governance',
            'governance',
            'compliance_forms',
        ],
        role_family: 'security_cyber',
        sectors: ['banking_payments', 'insurance', 'securities', 'crypto_assets'],
        primary_destinations: [
            'vendor_risk_portfolio',
            'contracts',
            'incidents',
            'continuity_exercises',
            'regulatory_sla',
        ],
    },
    {
        persona_id: 'P-EU-04',
        jurisdiction: 'EU',
        doc_slug: 'eu-nis2-entity-security-lead',
        persona_label: {
            en: 'NIS2 Entity Security & Incident Reporting Lead',
            vi: 'Trưởng an ninh & báo cáo sự cố theo NIS2',
        },
        primary_modules: [
            'incidents',
            'incident_ops',
            'program_governance',
            'vendor_risk',
            'compliance_forms',
            'monitoring',
        ],
        role_family: 'security_cyber',
        sectors: [
            'energy_utilities',
            'transport',
            'banking_payments',
            'health',
            'telecom_postal',
            'public_sector',
            'manufacturing_quality',
            'technology_saas',
        ],
        primary_destinations: ['incidents', 'regulatory_sla', 'vendor_risk_portfolio', 'training'],
    },
    // ── United Kingdom ───────────────────────────────────────────
    {
        persona_id: 'P-UK-01',
        jurisdiction: 'UK',
        doc_slug: 'uk-data-protection-officer',
        persona_label: {
            en: 'UK Data Protection Officer',
            vi: 'Cán bộ bảo vệ dữ liệu Vương quốc Anh',
        },
        primary_modules: [
            'data_mapping',
            'assessments',
            'rights_requests',
            'incidents',
            'consent',
            'compliance_forms',
        ],
        role_family: 'privacy_data_protection',
        sectors: ['cross_sector'],
        primary_destinations: [
            'processing_activities',
            'dpias',
            'breach_notifications',
            'marketing_consent',
            'cmp_integration',
        ],
    },
    {
        persona_id: 'P-UK-02',
        jurisdiction: 'UK',
        doc_slug: 'uk-nis-security-lead',
        persona_label: {
            en: 'UK NIS Operator Security Lead',
            vi: 'Trưởng an ninh nhà khai thác theo NIS Vương quốc Anh',
        },
        primary_modules: [
            'incidents',
            'incident_ops',
            'program_governance',
            'compliance_forms',
            'monitoring',
        ],
        role_family: 'security_cyber',
        sectors: ['energy_utilities', 'transport', 'health', 'telecom_postal', 'technology_saas'],
        primary_destinations: ['incidents', 'regulatory_sla', 'monitoring_programs'],
    },
    // ── Jurisdiction-independent ─────────────────────────────────
    // Never gated. ISO 27001 and SOC 2 belong to every organisation, so a rule
    // of "hide anything not matching my market" would hide exactly the two
    // frameworks that are nobody's jurisdiction and everybody's concern.
    {
        persona_id: 'P-GL-01',
        jurisdiction: 'GL',
        doc_slug: 'global-isms-manager',
        persona_label: {
            en: 'Information Security Management System Manager',
            vi: 'Quản lý hệ thống quản lý an toàn thông tin',
        },
        primary_modules: [
            'program_governance',
            'assessments',
            'audit_trail',
            'governance',
            'task_management',
            'compliance_forms',
        ],
        role_family: 'security_cyber',
        sectors: ['cross_sector'],
        primary_destinations: [
            'governance_documents',
            'audit_engagements',
            'remediation_plans',
            'evidence_gaps',
        ],
    },
    {
        persona_id: 'P-GL-02',
        jurisdiction: 'GL',
        doc_slug: 'global-soc2-audit-readiness-lead',
        persona_label: { en: 'SOC 2 Audit Readiness Lead', vi: 'Trưởng sẵn sàng kiểm toán SOC 2' },
        primary_modules: [
            'program_governance',
            'compliance_forms',
            'audit_trail',
            'assessments',
            'vendor_risk',
            'task_management',
        ],
        role_family: 'audit_legal_assurance',
        sectors: ['technology_saas'],
        primary_destinations: [
            'obligation_mappings',
            'audit_readiness',
            'evidence_gaps',
            'audit_engagements',
            'remediation_plans',
        ],
    },
];
/**
 * Fast lookup by persona ID.
 *
 * A null-prototype object (fix-sprint-20261017-2115 Finding 6): the key is often
 * untrusted (`?persona=` in a shared link), and on a plain object
 * `PERSONA_REGISTRY_BY_ID['constructor']` is `Object`, truthy and missing every
 * field — Persona Paths crashed rendering `entry.jurisdiction.toLowerCase()`.
 * With no prototype, every key that is not a persona id reads `undefined`.
 */
export const PERSONA_REGISTRY_BY_ID = Object.assign(Object.create(null), Object.fromEntries(PERSONA_REGISTRY.map((p) => [p.persona_id, p])));
/** Every registered persona ID, in registry order. */
export const ALL_PERSONA_IDS = PERSONA_REGISTRY.map((p) => p.persona_id);
//# sourceMappingURL=persona-registry.js.map